// BridgeTile — the iMessage/WhatsApp local-capture tile on the Connections page.
//
// The bridge isn't a cloud credential; it's the Reverra Mac app running on the
// owner's machine. So instead of a Connect button this tile shows live, PER-
// CHANNEL capture status derived from each reader's heartbeat (iMessage and
// WhatsApp are independent — separate readers, separate kill switches), points
// to the Mac setup in Settings, renders the WhatsApp pairing QR when mid-pair,
// and lets the owner pause/resume each channel on its own.
//
// Truthful status: a dead reader shows "Not running — last seen Xh ago" (red),
// a one-beat-late reader shows amber "Delayed", a reader that's alive but erroring
// (iMessage with no Full Disk Access) surfaces that error, and a paused channel
// shows "Paused" — never a stale green "connected". This is what makes a
// silently-down channel visible.

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import QRCode from 'react-qr-code';
import { Smartphone, ArrowRight, DownloadCloud, Loader2, Check, Pause, Play } from 'lucide-react';
import {
  readAllHeartbeats,
  classifyHeartbeat,
  type HeartbeatPayload,
} from '@reverra/shared/heartbeat/read';
import GlassCard from '@/components/shared/GlassCard';
import PulseDot from '@/components/shared/PulseDot';
import { supabase } from '@/lib/supabase';

type BridgeMetadata = {
  whatsapp_qr?: string | null;
  import_status?: string | null;
  import_count?: number | null;
};

export type BridgeRow =
  | {
      status: string;
      metadata: BridgeMetadata | null;
      last_error: string | null;
    }
  | undefined;

type PulseColor = 'green' | 'amber' | 'red';
type HbStatus = 'alive' | 'asleep' | 'warning' | 'alert' | 'unknown';
type ChannelHealth = { status: HbStatus; lastError: string | null; lastSeen: string | null };

const IMESSAGE_SWITCH = 'bridge_imessage_enabled';
const WHATSAPP_SWITCH = 'bridge_whatsapp_enabled';

/** A bare duration — ago() appends "ago", which reads wrong after "asleep for". */
function sleptFor(iso: string | null): string {
  if (!iso) return 'a while';
  try {
    return formatDistanceToNow(new Date(iso));
  } catch {
    return 'a while';
  }
}

function ago(iso: string | null): string {
  if (!iso) return 'unknown';
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return 'unknown';
  }
}

// iMessage's "unable to open database file" / "chat.db not found" both mean the
// reader lacks Full Disk Access — say that plainly instead of the raw sqlite error.
function humanizeError(err: string): string {
  if (/full disk access|unable to open|chat\.db/i.test(err)) {
    return 'Needs Full Disk Access on the Mac';
  }
  return err.length > 80 ? `${err.slice(0, 80)}…` : err;
}

// Module-scoped so its identity is stable across BridgeTile re-renders (a heartbeat
// refetch / now-tick every 15-30s) — defining it inside would remount the rows and
// drop button focus.
function ChannelRow({
  label,
  tone,
  text,
  enabled,
  onToggle,
  isPending,
}: {
  label: string;
  tone: PulseColor;
  text: string;
  enabled: boolean | undefined; // undefined = switch not loaded yet
  onToggle: () => void;
  isPending: boolean;
}): JSX.Element {
  const loading = enabled === undefined;
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-2 text-[11px]">
        <PulseDot color={tone} size="sm" />
        <span className="text-foreground">{label}:</span>
        <span className="truncate text-muted-foreground">{text}</span>
      </div>
      <button
        type="button"
        onClick={onToggle}
        disabled={isPending || loading}
        aria-label={`${enabled ? 'Pause' : 'Resume'} ${label} capture`}
        className="neo-chip inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] text-foreground transition-colors disabled:opacity-60"
      >
        {enabled ? <Pause className="h-2.5 w-2.5" /> : <Play className="h-2.5 w-2.5" />}
        {enabled ? 'Pause' : 'Resume'}
      </button>
    </div>
  );
}

export default function BridgeTile({ row }: { row: BridgeRow }): JSX.Element {
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 15_000);
    return () => window.clearInterval(id);
  }, []);

  const hbQ = useQuery({
    queryKey: ['heartbeats'],
    queryFn: () => readAllHeartbeats(supabase),
    // 10s (not 30s): the bridge heartbeats every 30s, so a 30s refetch made a
    // HEALTHY reader's displayed staleness reach ~60s and trip the 45s "warning"
    // threshold — showing amber "Delayed" constantly. 10s keeps it well under.
    refetchInterval: 10_000,
    retry: 0,
  });
  const rows = hbQ.data ?? [];
  const health = (name: string): ChannelHealth => {
    const r = rows.find((x) => x.daemon_name === name);
    const payload = r?.payload as HeartbeatPayload | null | undefined;
    if (!payload) return { status: 'unknown', lastError: null, lastSeen: null };
    return {
      // daemonName lets a capture reader whose Mac is simply asleep report
      // 'asleep' instead of 'alert' — this tile said "Not running" every
      // morning while capture was healthy and merely waiting for the lid.
      status: classifyHeartbeat(payload, now, { daemonName: name }).status,
      lastError: payload.last_error ?? null,
      lastSeen: payload.last_seen_at ?? null,
    };
  };
  const imessage = health('bridge_imessage');
  const whatsapp = health('bridge_whatsapp');
  // The local desktop-store reader is the DEFAULT WhatsApp backend. It has no
  // pairing step, so it never writes a QR or a 'connected' connections row —
  // its heartbeat is the only signal it exists. Without checking it here, a
  // machine running the default backend renders as "Not set up" forever, and a
  // genuine outage looks identical to that. This tile's whole purpose is making
  // a silently-down channel visible, so it has to know about both backends.
  const whatsappLocal = health('bridge_whatsapp_local');
  const qr = row?.metadata?.whatsapp_qr ?? null;
  const waConnected = row?.status === 'connected';

  // Per-channel kill switches (migration 0042). Undefined until the row loads —
  // we render a neutral "Checking…" rather than flash green/Paused.
  const qc = useQueryClient();
  const switchQ = useQuery({
    queryKey: ['bridge-channel-switches'],
    queryFn: async (): Promise<Record<string, boolean>> => {
      const { data } = await supabase
        .from('app_settings')
        .select('key, value')
        .in('key', [IMESSAGE_SWITCH, WHATSAPP_SWITCH]);
      const map: Record<string, boolean> = {};
      for (const r of data ?? []) map[r.key as string] = (r.value as string) !== 'false';
      return map;
    },
    retry: 0,
  });
  const imessageEnabled = switchQ.data?.[IMESSAGE_SWITCH];
  const whatsappEnabled = switchQ.data?.[WHATSAPP_SWITCH];

  const toggleMut = useMutation({
    mutationFn: async ({ key, next }: { key: string; next: boolean }): Promise<void> => {
      const { data, error } = await supabase
        .from('app_settings')
        .update({ value: next ? 'true' : 'false' })
        .eq('key', key)
        .select('key');
      if (error) throw error;
      if (!data || data.length === 0) throw new Error(`Toggle affected zero rows for ${key}`);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['bridge-channel-switches'] }),
  });
  const toggleChannel = (key: string, enabled: boolean | undefined): void => {
    if (enabled === undefined) return;
    toggleMut.reset(); // clear any prior error before the new attempt
    toggleMut.mutate({ key, next: !enabled });
  };

  type ChannelStatus = { tone: PulseColor; text: string; hint: string | null };

  const imessageStatus = ((): ChannelStatus => {
    if (imessageEnabled === undefined) return { tone: 'amber', text: 'Checking…', hint: null };
    if (!imessageEnabled) return { tone: 'amber', text: 'Paused', hint: null };
    if (imessage.status === 'alive' && imessage.lastError) {
      const fda = /full disk access|unable to open|chat\.db/i.test(imessage.lastError);
      return {
        tone: 'amber',
        text: humanizeError(imessage.lastError),
        hint: fda
          ? 'Grant the bridge Full Disk Access (System Settings → Privacy & Security → Full Disk Access), then it resumes on its own.'
          : 'Check the bridge logs on the Mac (.bridge-launchd.err.log).',
      };
    }
    // A capture reader whose Mac is simply shut. Green, not amber: nothing is
    // wrong and nothing needs doing — amber would read as "act on me".
    if (imessage.status === 'asleep')
      return {
        tone: 'green',
        text: `Asleep — your Mac has been shut for ${sleptFor(imessage.lastSeen)}`,
        hint: 'Capture resumes on its own when you open it, and backfills anything missed.',
      };
    if (imessage.status === 'alive') return { tone: 'green', text: 'Capturing', hint: null };

    if (imessage.status === 'warning')
      return { tone: 'amber', text: `Delayed — last seen ${ago(imessage.lastSeen)}`, hint: null };
    return {
      tone: 'red',
      text: imessage.lastSeen
        ? `Not running — last seen ${ago(imessage.lastSeen)}`
        : 'Not running — set up the Mac bridge',
      hint: 'Start the bridge on your Mac (it captures while the Mac is awake + plugged in).',
    };
  })();

  const whatsappStatus = ((): ChannelStatus => {
    if (whatsappEnabled === undefined) return { tone: 'amber', text: 'Checking…', hint: null };
    if (!whatsappEnabled) return { tone: 'amber', text: 'Paused', hint: null };

    // Local desktop-store backend (the default) — reported first because when
    // it is running there is no QR and nothing to pair.
    // A capture reader whose Mac is simply shut. Green, not amber: nothing is
    // wrong and nothing needs doing — amber would read as "act on me".
    if (whatsappLocal.status === 'asleep')
      return {
        tone: 'green',
        text: `Asleep — your Mac has been shut for ${sleptFor(whatsappLocal.lastSeen)}`,
        hint: 'Capture resumes on its own when you open it, and backfills anything missed.',
      };
    if (whatsappLocal.status === 'alive') {
      if (whatsappLocal.lastError)
        return {
          tone: 'amber',
          text: humanizeError(whatsappLocal.lastError),
          hint: 'Open the WhatsApp app on this Mac and make sure you are signed in. Reverra reads its messages directly — there is no QR code to scan.',
        };
      return { tone: 'green', text: 'Capturing from the WhatsApp app', hint: null };
    }
    if (whatsappLocal.status === 'warning')
      return {
        tone: 'amber',
        text: `Delayed — last seen ${ago(whatsappLocal.lastSeen)}`,
        hint: null,
      };
    // Local reader has run before but has now gone silent — a real outage, and
    // distinguishable from "never set up" because we have a last-seen time.
    if (whatsappLocal.lastSeen && whatsapp.status !== 'alive')
      return {
        tone: 'red',
        text: `Not running — last seen ${ago(whatsappLocal.lastSeen)}`,
        hint: 'Start Reverra Capture on your Mac. It resumes on its own — nothing to re-scan.',
      };

    if (whatsapp.status === 'alive' && waConnected)
      return { tone: 'green', text: 'Paired & capturing', hint: null };
    if (whatsapp.status === 'alive' && qr)
      return { tone: 'amber', text: 'Waiting for you to scan the QR ↓', hint: null };
    if (whatsapp.status === 'alive' && whatsapp.lastError)
      return {
        tone: 'amber',
        text: humanizeError(whatsapp.lastError),
        hint: 'If it stays stuck, re-pair: WhatsApp → Linked Devices → Log out, then start the bridge and scan again.',
      };
    // Same class as the two above: the legacy linked-device reader also lives on
    // the Mac, so a shut lid must not read as "Not running" here either.
    if (whatsapp.status === 'asleep')
      return {
        tone: 'green',
        text: `Asleep — your Mac has been shut for ${sleptFor(whatsapp.lastSeen)}`,
        hint: 'Capture resumes on its own when you open it, and backfills anything missed.',
      };
    if (whatsapp.status === 'alive') return { tone: 'amber', text: 'Setting up…', hint: null };
    if (whatsapp.status === 'warning')
      return { tone: 'amber', text: `Delayed — last seen ${ago(whatsapp.lastSeen)}`, hint: null };
    return {
      tone: 'red',
      text: whatsapp.lastSeen ? `Not running — last seen ${ago(whatsapp.lastSeen)}` : 'Not set up',
      hint: whatsapp.lastSeen
        ? 'Start the bridge on your Mac — it reconnects automatically (session stays paired).'
        : 'Start the bridge on your Mac, then scan the pairing QR that appears here.',
    };
  })();

  // Import only makes sense when WhatsApp is actually running + paired.
  const waCapturing = waConnected && whatsapp.status === 'alive' && whatsappEnabled === true;

  // History import — live status polled from the bridge connection metadata.
  const [armed, setArmed] = useState(false);
  const [requested, setRequested] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const importQ = useQuery({
    queryKey: ['bridge-import-status'],
    queryFn: async (): Promise<BridgeMetadata | null> => {
      const { data } = await supabase
        .from('connections')
        .select('metadata')
        .eq('provider', 'bridge')
        .maybeSingle();
      return (data?.metadata ?? null) as BridgeMetadata | null;
    },
    refetchInterval: (q) => {
      const s = (q.state.data as BridgeMetadata | null | undefined)?.import_status;
      return s === 'importing' || armed ? 5_000 : false;
    },
    retry: 0,
  });
  const importStatus = importQ.data?.import_status ?? row?.metadata?.import_status ?? null;
  const importCount = importQ.data?.import_count ?? row?.metadata?.import_count ?? 0;
  const importing = importStatus === 'importing';
  const importedNothing = requested && importStatus === 'complete' && importCount === 0;
  const importedSome = requested && importStatus === 'complete' && importCount > 0;

  useEffect(() => {
    if (importStatus === 'complete') setArmed(false);
  }, [importStatus]);

  const importMut = useMutation({
    mutationFn: async (): Promise<void> => {
      const { data: u } = await supabase.auth.getUser();
      if (!u?.user?.id) throw new Error('No active session');
      const payload = JSON.stringify({
        requested_at: new Date().toISOString(),
        requested_by: u.user.id,
      });
      const { data, error } = await supabase
        .from('app_settings')
        .update({ value: payload })
        .eq('key', 'bridge_import_request')
        .select('key');
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error('Import request affected zero rows (RLS or seed mismatch)');
      }
    },
    onSuccess: () => {
      setImportError(null);
      setRequested(true);
      setArmed(true);
      void qc.invalidateQueries({ queryKey: ['bridge-import-status'] });
    },
    onError: (e) => setImportError(e instanceof Error ? e.message : 'Could not request import'),
  });

  return (
    <GlassCard hover={false}>
      <div className="flex items-start gap-4">
        <div className="neo-chip w-10 h-10 rounded-lg flex items-center justify-center shrink-0">
          <Smartphone className="w-5 h-5 text-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-foreground">iMessage &amp; WhatsApp</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Captured locally by the Reverra Mac app. Read-only — Reverra never sends messages. Each
            channel runs and pauses independently.
          </p>
          <div className="mt-2 space-y-1.5">
            <ChannelRow
              label="iMessage / SMS"
              tone={imessageStatus.tone}
              text={imessageStatus.text}
              enabled={imessageEnabled}
              isPending={toggleMut.isPending}
              onToggle={() => toggleChannel(IMESSAGE_SWITCH, imessageEnabled)}
            />
            <ChannelRow
              label="WhatsApp"
              tone={whatsappStatus.tone}
              text={whatsappStatus.text}
              enabled={whatsappEnabled}
              isPending={toggleMut.isPending}
              onToggle={() => toggleChannel(WHATSAPP_SWITCH, whatsappEnabled)}
            />
          </div>
          {toggleMut.isError && (
            <p className="mt-1 text-[10px] text-alert">Couldn’t change the channel — try again.</p>
          )}

          {(imessageStatus.hint || whatsappStatus.hint) && (
            <div className="neo-inset mt-2 rounded-md p-2 text-[10px] leading-snug text-muted-foreground">
              <p className="font-medium text-foreground">How to fix</p>
              {imessageStatus.hint && (
                <p className="mt-0.5">
                  <span className="text-foreground">iMessage</span> — {imessageStatus.hint}
                </p>
              )}
              {whatsappStatus.hint && (
                <p className="mt-0.5">
                  <span className="text-foreground">WhatsApp</span> — {whatsappStatus.hint}
                </p>
              )}
              <p className="mt-1">
                Always-on (survives reboots):{' '}
                <code className="rounded bg-background px-1 py-0.5">
                  ./scripts/install-bridge-launchagent.sh
                </code>
              </p>
            </div>
          )}

          {qr && !waConnected && whatsappEnabled && (
            <div className="mt-3">
              <p className="text-[11px] text-muted-foreground mb-1.5">
                On your phone: WhatsApp → Linked devices → Link a device, then scan:
              </p>
              <div className="inline-block bg-white p-2 rounded-md">
                <QRCode value={qr} size={140} />
              </div>
            </div>
          )}

          {waCapturing && (
            <div className="mt-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => importMut.mutate()}
                  disabled={importMut.isPending || importing}
                  aria-label="Import recent WhatsApp history"
                  className="neo-chip inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[11px] font-medium text-foreground transition-colors disabled:opacity-60"
                >
                  {importing ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <DownloadCloud className="w-3 h-3" />
                  )}
                  {importing ? 'Importing…' : 'Import recent history'}
                </button>
                {importing && (
                  <span aria-live="polite" className="text-[11px] text-muted-foreground">
                    {importCount} so far
                  </span>
                )}
                {importedSome && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-forest">
                    <Check className="w-3 h-3" /> Imported {importCount}
                  </span>
                )}
              </div>
              <p className="mt-1 text-[10px] text-muted-foreground">
                WhatsApp only releases history when you link a device — this pulls the recent window
                it shares, most complete right after pairing. Messages flow into Messages as they
                parse.
              </p>
              {importedNothing && (
                <p className="mt-1 text-[10px] text-attention">
                  No history was released — already-linked sessions get little. Re-link your phone
                  (Linked Devices → Log out → re-pair) to backfill recent conversations.
                </p>
              )}
              {importError && <p className="mt-1 text-[10px] text-alert">{importError}</p>}
            </div>
          )}
        </div>

        <div className="shrink-0">
          <Link
            to="/settings"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            Set up on Mac <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </GlassCard>
  );
}
